// A plain ZIP archive, written and read without any library (Owner 2026-10-20: an unfinished inspection is
// carried to another device in ONE file). ZIP because every phone and computer already knows the format:
// AirDrop, Files, Drive, e-mail and a USB cable all pass it along, and the system can open it.
//
// Only what this product needs: entries are STORED (photos and PDFs are already compressed), names are UTF-8,
// there is no ZIP64 (the archive stays under 4 GB, MAX_ARCHIVE_BYTES), and every entry carries its CRC-32,
// which the reader checks — a file damaged on the way is refused, never half-imported.
const LOCAL_HEADER = 0x04034b50;
const CENTRAL_HEADER = 0x02014b50;
const END_OF_CENTRAL = 0x06054b50;
const UTF8_FLAG = 0x0800;
const VERSION = 20;

export const MAX_ARCHIVE_BYTES = 0xffff_fff0;
export const MAX_ENTRIES = 0xffff;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function dosTime(date: Date): { time: number; date: number } {
  const year = Math.min(2107, Math.max(1980, date.getFullYear()));
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate()
  };
}

const encoder = new TextEncoder();

export class ZipLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ZipLimitError';
  }
}

// Writes the archive as a stream of chunks: each entry's header and bytes go out as soon as they are added,
// so a phone never has to hold the whole archive in memory (src/native/packageFile.ts appends them to a file).
export class ZipWriter {
  private offset = 0;
  private readonly central: Uint8Array[] = [];
  private count = 0;

  constructor(private readonly emit: (chunk: Uint8Array) => Promise<void>) {}

  get bytesWritten(): number {
    return this.offset;
  }

  async add(name: string, bytes: Uint8Array, modified: Date = new Date()): Promise<void> {
    if (this.count >= MAX_ENTRIES) throw new ZipLimitError('too many files for one archive');
    const nameBytes = encoder.encode(name);
    if (this.offset + 30 + nameBytes.length + bytes.length > MAX_ARCHIVE_BYTES) throw new ZipLimitError('the archive would be larger than 4 GB');
    const crc = crc32(bytes);
    const stamp = dosTime(modified);
    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, LOCAL_HEADER, true);
    lv.setUint16(4, VERSION, true);
    lv.setUint16(6, UTF8_FLAG, true);
    lv.setUint16(8, 0, true);
    lv.setUint16(10, stamp.time, true);
    lv.setUint16(12, stamp.date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, bytes.length, true);
    lv.setUint32(22, bytes.length, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);
    local.set(nameBytes, 30);

    const entry = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(entry.buffer);
    cv.setUint32(0, CENTRAL_HEADER, true);
    cv.setUint16(4, VERSION, true);
    cv.setUint16(6, VERSION, true);
    cv.setUint16(8, UTF8_FLAG, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, stamp.time, true);
    cv.setUint16(14, stamp.date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, bytes.length, true);
    cv.setUint32(24, bytes.length, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true);
    cv.setUint16(32, 0, true);
    cv.setUint16(34, 0, true);
    cv.setUint16(36, 0, true);
    cv.setUint32(38, 0, true);
    cv.setUint32(42, this.offset, true);
    entry.set(nameBytes, 46);

    await this.emit(local);
    if (bytes.length > 0) await this.emit(bytes);
    this.offset += local.length + bytes.length;
    this.central.push(entry);
    this.count += 1;
  }

  async finish(): Promise<void> {
    const size = this.central.reduce((total, entry) => total + entry.length, 0);
    const directory = new Uint8Array(size + 22);
    let at = 0;
    for (const entry of this.central) {
      directory.set(entry, at);
      at += entry.length;
    }
    const ev = new DataView(directory.buffer, size);
    ev.setUint32(0, END_OF_CENTRAL, true);
    ev.setUint16(4, 0, true);
    ev.setUint16(6, 0, true);
    ev.setUint16(8, this.count, true);
    ev.setUint16(10, this.count, true);
    ev.setUint32(12, size, true);
    ev.setUint32(16, this.offset, true);
    ev.setUint16(20, 0, true);
    await this.emit(directory);
    this.offset += directory.length;
  }
}

// ---------- reading ----------

export interface ZipEntry {
  name: string;
  size: number;
  crc: number;
  localOffset: number;
}

export class ZipFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ZipFormatError';
  }
}

// What the reader needs from the file: its size and a slice of bytes. A File / Blob in the app, a buffer in tests.
export interface ByteSource {
  size: number;
  read(start: number, end: number): Promise<Uint8Array>;
}

export function blobSource(blob: Blob): ByteSource {
  return {
    size: blob.size,
    async read(start, end) {
      const part = blob.slice(start, end);
      if (typeof part.arrayBuffer === 'function') return new Uint8Array(await part.arrayBuffer());
      return new Uint8Array(
        await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as ArrayBuffer);
          reader.onerror = () => reject(reader.error ?? new Error('read failed'));
          reader.readAsArrayBuffer(part);
        })
      );
    }
  };
}

const decoder = new TextDecoder('utf-8', { fatal: false });

export async function readZipDirectory(source: ByteSource): Promise<ZipEntry[]> {
  if (source.size < 22) throw new ZipFormatError('not a zip file');
  const tailStart = Math.max(0, source.size - (22 + 0xffff));
  const tail = await source.read(tailStart, source.size);
  let end = -1;
  for (let i = tail.length - 22; i >= 0; i -= 1) {
    if (tail[i] === 0x50 && tail[i + 1] === 0x4b && tail[i + 2] === 0x05 && tail[i + 3] === 0x06) {
      end = i;
      break;
    }
  }
  if (end < 0) throw new ZipFormatError('not a zip file');
  const ev = new DataView(tail.buffer, tail.byteOffset + end, 22);
  const count = ev.getUint16(10, true);
  const size = ev.getUint32(12, true);
  const offset = ev.getUint32(16, true);
  if (offset + size > source.size) throw new ZipFormatError('the file is cut short');
  const directory = await source.read(offset, offset + size);
  const view = new DataView(directory.buffer, directory.byteOffset, directory.byteLength);
  const entries: ZipEntry[] = [];
  let at = 0;
  for (let i = 0; i < count; i += 1) {
    if (at + 46 > directory.length || view.getUint32(at, true) !== CENTRAL_HEADER) throw new ZipFormatError('damaged file list');
    const method = view.getUint16(at + 10, true);
    const crc = view.getUint32(at + 16, true);
    const compressed = view.getUint32(at + 20, true);
    const plain = view.getUint32(at + 24, true);
    const nameLength = view.getUint16(at + 28, true);
    const extraLength = view.getUint16(at + 30, true);
    const commentLength = view.getUint16(at + 32, true);
    const localOffset = view.getUint32(at + 42, true);
    if (method !== 0 || compressed !== plain) throw new ZipFormatError('this file was repacked; use the file exactly as it was exported');
    if (at + 46 + nameLength > directory.length) throw new ZipFormatError('damaged file list');
    const name = decoder.decode(directory.subarray(at + 46, at + 46 + nameLength));
    if (localOffset + 30 + plain > source.size) throw new ZipFormatError('the file is cut short');
    entries.push({ name, size: plain, crc, localOffset });
    at += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

export async function readZipEntry(source: ByteSource, entry: ZipEntry): Promise<Uint8Array> {
  const header = await source.read(entry.localOffset, entry.localOffset + 30);
  const hv = new DataView(header.buffer, header.byteOffset, header.byteLength);
  if (header.length < 30 || hv.getUint32(0, true) !== LOCAL_HEADER) throw new ZipFormatError('damaged entry');
  const start = entry.localOffset + 30 + hv.getUint16(26, true) + hv.getUint16(28, true);
  if (start + entry.size > source.size) throw new ZipFormatError('the file is cut short');
  const bytes = await source.read(start, start + entry.size);
  if (bytes.length !== entry.size || crc32(bytes) !== entry.crc) throw new ZipFormatError(`damaged entry ${entry.name}`);
  return bytes;
}
